import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAtsEmbedActive } from '../api/ats-embed';
/**
 * Keep in-app navigation inside the ATS iframe.
 *
 * Standalone, the dashboard opens job descriptions, schedules and reports in a new
 * tab — around thirty `target="_blank"` anchors and `window.open` calls spread
 * across a dozen files. Embedded in a client's ATS that is wrong twice over: the
 * recruiter is thrown out of the host product into a bare tab, and that tab is a
 * fresh page load outside the embed, with no sidebar and no way back.
 *
 * Rather than rewrite every call site (and rely on nobody adding another), embed
 * mode intercepts the two ways a tab gets opened and turns SAME-ORIGIN ones into
 * client-side navigation. Cross-origin links — the Vriti recruiter portal,
 * recording URLs — still open a tab, which is right for those.
 *
 * No-op unless the page is an active ATS embed, so standalone behaviour is
 * untouched.
 */
/**
 * Only the first mounted instance installs the guard. `App` renders the login
 * widget, which wraps another `App`, so two can be mounted at once — and two
 * listeners would push the same route twice and leave a duplicate history entry
 * behind the back button. React unmounts children first, so the flag is released
 * by the same instance that set it.
 */
let installed = false;
export const useEmbedNavigation = () => {
    const navigate = useNavigate();
    useEffect(() => {
        if (!isAtsEmbedActive() || installed)
            return undefined;
        installed = true;
        /** Same-origin URL → the router path to push; null when it isn't ours. */
        const toInternalPath = (url) => {
            try {
                const target = new URL(url, window.location.href);
                if (target.origin !== window.location.origin)
                    return null;
                return `${target.pathname}${target.search}${target.hash}`;
            }
            catch (_a) {
                return null; // unparseable (about:blank, mailto:, …) — leave it alone
            }
        };
        const onClick = (event) => {
            var _a, _b;
            // Respect an explicit "open in a new tab" (ctrl/cmd/shift/middle click).
            if (event.defaultPrevented || event.button !== 0)
                return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
                return;
            const anchor = (_b = (_a = event.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, 'a');
            if (!anchor || anchor.target !== '_blank')
                return;
            const path = toInternalPath(anchor.getAttribute('href') || '');
            if (!path)
                return; // external link — let the browser open its tab
            event.preventDefault();
            navigate(path);
        };
        // Capture phase: run before a handler that stops propagation.
        document.addEventListener('click', onClick, true);
        const originalOpen = window.open;
        window.open = function patchedOpen(url, ...rest) {
            const path = url ? toInternalPath(String(url)) : null;
            if (path) {
                navigate(path);
                return null;
            }
            return originalOpen.call(window, url, ...rest);
        };
        return () => {
            document.removeEventListener('click', onClick, true);
            window.open = originalOpen;
            installed = false;
        };
    }, [navigate]);
};
export default useEmbedNavigation;
